import React, { useCallback, useMemo } from 'react';
import { Link } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import routesConfig from 'constants/routesConfig.json';
import colours from 'theme/atoms/colours';
import { usePosthogCapture } from 'auth/posthogCapture/_index';

import useCountdown from 'auth/hooks/useCountdown';

import * as Styled from 'auth/styles/ErrorMessage.style';
import * as Text from 'theme/components/Text.style';
import { useLoginMutation } from 'auth/hooks/loginApi';

type Action = {
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};

type ErrorMessageViewProps =
  | {
      kind: 'invalidEmail';
      title: string;
      items: React.ReactNode[];
    }
  | {
      kind: 'invalidCode';
      title: string;
      returnToEmailLabel: string;
      onReturnToEmail: () => void;
      orLabel: string;
      reEnterCodeLabel: string;
      onReEnterCode?: () => void;
    }
  | {
      kind: 'tooManyAttempts';
      title: string;
      action: Action;
    }
  | {
      kind: 'missingEmail';
      label: string;
      action: Action;
    };

function ErrorMessageView(props: ErrorMessageViewProps): React.ReactElement {
  const { kind } = props;

  switch (kind) {
    case 'invalidEmail': {
      const { title, items } = props;
      return (
        <Styled.Error>
          <Styled.ErrorMessage>{title}</Styled.ErrorMessage>
          <Styled.ErrorMessageList>
            {items.map((item, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <Styled.ErrorMessageSmall key={idx}>
                {item}
              </Styled.ErrorMessageSmall>
            ))}
          </Styled.ErrorMessageList>
        </Styled.Error>
      );
    }

    case 'invalidCode': {
      const {
        title,
        onReturnToEmail,
        returnToEmailLabel,
        orLabel,
        onReEnterCode,
        reEnterCodeLabel,
      } = props;

      return (
        <Styled.Error>
          <Styled.ErrorMessage>{title}</Styled.ErrorMessage>
          <Styled.ErrorMessage
            as="button"
            onClick={onReturnToEmail}
            style={{ color: colours.primary[400], cursor: 'pointer' }}
          >
            {returnToEmailLabel}
          </Styled.ErrorMessage>
          <Styled.ErrorMessage>{orLabel}</Styled.ErrorMessage>
          <Styled.ErrorMessage
            as="button"
            onClick={onReEnterCode}
            style={{ color: colours.primary[400], cursor: 'pointer' }}
          >
            {reEnterCodeLabel}
          </Styled.ErrorMessage>
        </Styled.Error>
      );
    }

    case 'tooManyAttempts': {
      const { title, action } = props;
      const { disabled, label, onClick } = action;

      return (
        <Styled.Error>
          <Styled.ErrorMessage>{title}</Styled.ErrorMessage>
          {disabled ? (
            <Styled.ErrorMessage>{label}</Styled.ErrorMessage>
          ) : (
            <Styled.ErrorMessage
              as="button"
              onClick={onClick}
              style={{ color: colours.primary[400], cursor: 'pointer' }}
            >
              {label}
            </Styled.ErrorMessage>
          )}
        </Styled.Error>
      );
    }

    case 'missingEmail': {
      const { label, action } = props;
      const { disabled, onClick, label: actionLabel } = action;

      return (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Text.P1Semibold>{label}</Text.P1Semibold>
          {disabled ? (
            <Styled.ResendTimed>{actionLabel}</Styled.ResendTimed>
          ) : (
            <Styled.Resend as="button" onClick={onClick}>
              {actionLabel}
            </Styled.Resend>
          )}
        </div>
      );
    }

    default: {
      // Exhaustiveness check (should be unreachable)
      return <div />;
    }
  }
}

interface ErrorMessageProps {
  action?: () => void;
  email?: string;
  error?: string;
}

export default function ErrorMessage({
  action,
  email,
  error,
}: ErrorMessageProps): React.ReactElement {
  const { t } = useTranslation('pageLogin');
  const auth = usePosthogCapture();
  const history = useHistory();
  const { mutate: login } = useLoginMutation();

  const shouldCountdown = error === 'too many attempts' || !error;
  const { timeLeft, reset } = useCountdown(60, { isActive: shouldCountdown });

  const handleEmailResend = useCallback(() => {
    login({ email });
    auth.loginAccessCodeRerequested();
    reset(60);
  }, [login, email, auth, reset]);

  const returnToEmailCard = useCallback(() => {
    history.push({ pathname: `${routesConfig.LOGIN_PAGE}` });
  }, [history]);

  const invalidEmailItems = useMemo(
    () => [
      t('errorMessages.invalidEmailAction1'),
      <Trans
        i18nKey="errorMessages.invalidEmailAction2"
        key="0"
        components={[
          <Link
            key="0"
            href="mailto:info@nurau.com"
            underline="none"
            target="_blank"
          />,
        ]}
        ns="pageLogin"
      />,
      <Trans
        i18nKey="errorMessages.invalidEmailAction3"
        key="1"
        components={[
          <Link
            key="0"
            href="https://us10.list-manage.com/survey?u=e450832626b2b44f0f73d73af&id=adb1f13f45&attribution=false"
            underline="none"
            target="_blank"
          />,
        ]}
        ns="pageLogin"
      />,
    ],
    [t]
  );

  if (error === 'invalid email') {
    return (
      <ErrorMessageView
        kind="invalidEmail"
        title={t('errorMessages.invalidEmailLabel')}
        items={invalidEmailItems}
      />
    );
  }

  if (error === 'invalid code') {
    return (
      <ErrorMessageView
        kind="invalidCode"
        title={t('errorMessages.expiredCodeLabel')}
        returnToEmailLabel={t('errorMessages.expiredCodeAction')}
        onReturnToEmail={returnToEmailCard}
        orLabel={t('errorMessages.or')}
        reEnterCodeLabel={t('errorMessages.reEnterCode')}
        onReEnterCode={action}
      />
    );
  }

  if (error === 'too many attempts') {
    return (
      <ErrorMessageView
        kind="tooManyAttempts"
        title={t('errorMessages.tooManyAttemptsLabel')}
        action={
          timeLeft === 0
            ? {
                label: t('errorMessages.expiredCodeAction'),
                onClick: returnToEmailCard,
              }
            : {
                label: t('errorMessages.tooManyAttemptsAction', { timeLeft }),
                disabled: true,
              }
        }
      />
    );
  }

  return (
    <ErrorMessageView
      kind="missingEmail"
      label={t('errorMessages.missingEmailLabel')}
      action={
        timeLeft === 0
          ? {
              label: t('errorMessages.missingEmailAction'),
              onClick: handleEmailResend,
            }
          : {
              label: t('errorMessages.missingEmailActionTimed', { timeLeft }),
              disabled: true,
            }
      }
    />
  );
}
